/**
 * Debug-only persistence for a runtime override of the main API base URL.
 *
 * The override is stored separately from the build-time default (CONFIG.VITE_HOST_API)
 * so it can be applied on top of whatever the current build ships with, and cleanly
 * removed with "Reset to default". Read synchronously at SystemStore creation so the
 * override is in place before the first API request.
 *
 * Set/cleared from the Debug Admin Panel's "Config & API" tab.
 *
 * **A production build never honours it.** A saved value would point a payment
 * POS at another backend no matter which environment file it was built from,
 * bypassing every build check (and the Debug panel's own access gate, which
 * protects the button, not the saved value). So in a build from
 * `environment.prod.ts` the getter always answers null, the setter refuses, and
 * a value left behind by another build is removed on load.
 */

import { CONFIG } from 'src/environments/environment';

const STORAGE_KEY = 'api_base_url_override';

/**
 * False in every production build: the override is a dev/staging tool only. A
 * configuration that does not say which environment it is counts as production.
 */
export const API_BASE_URL_OVERRIDE_ALLOWED: boolean = CONFIG.VITE_CURRENT_ENV?.isProd === false;

function removeStoredOverride(): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to remove API base URL override:', error);
  }
}

// A production build drops whatever another build saved, once, at load.
if (!API_BASE_URL_OVERRIDE_ALLOWED) removeStoredOverride();

/**
 * Returns the persisted API base URL override, or null when none is set,
 * when this is a production build, or when localStorage is unavailable
 * (e.g. SSR / privacy mode).
 */
export function getApiBaseUrlOverride(): string | null {
  if (!API_BASE_URL_OVERRIDE_ALLOWED) return null;
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value && value.length ? value : null;
  } catch (error) {
    console.error('Failed to read API base URL override:', error);
    return null;
  }
}

/**
 * Persists the API base URL override. Passing null (or an empty string)
 * removes the override so the app falls back to the build-time default.
 * A production build only ever removes it.
 */
export function setApiBaseUrlOverrideStorage(url: string | null): void {
  if (!API_BASE_URL_OVERRIDE_ALLOWED) {
    removeStoredOverride();
    return;
  }
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (url && url.length) {
      window.localStorage.setItem(STORAGE_KEY, url);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    console.error('Failed to write API base URL override:', error);
  }
}
